import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="tab-bar"
// In-event section nav (event-overview.html §02 .evo-tabnav).
// Desktop: a horizontal tab row that scrolls — bring the active tab into view
// so the current section isn't hidden off-screen, and show ‹ / › overflow
// affordances when the row is wider than the bar. Mobile (≤720px): the row
// collapses to a "current ▾" dropdown; toggle() opens/closes it.
export default class extends Controller {
  static targets = ["scroller", "active", "trigger"]

  connect() {
    this.scrollActiveIntoView()
    this.updateScrollState()
    // Close the mobile dropdown when clicking outside it.
    this.onDocClick = (e) => {
      if (!this.element.contains(e.target)) this.close()
    }
    document.addEventListener("click", this.onDocClick)
    // Keep the overflow affordances in sync as the row scrolls / the viewport
    // resizes.
    this.onScroll = () => this.updateScrollState()
    this.onResize = () => this.updateScrollState()
    if (this.hasScrollerTarget) {
      this.scrollerTarget.addEventListener("scroll", this.onScroll, { passive: true })
    }
    window.addEventListener("resize", this.onResize)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick)
    if (this.hasScrollerTarget) {
      this.scrollerTarget.removeEventListener("scroll", this.onScroll)
    }
    window.removeEventListener("resize", this.onResize)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    const open = this.element.classList.toggle("open")
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", open ? "true" : "false")
    }
  }

  close() {
    if (!this.element.classList.contains("open")) return
    this.element.classList.remove("open")
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute("aria-expanded", "false")
  }

  scrollActiveIntoView() {
    if (!this.hasActiveTarget || !this.hasScrollerTarget) return
    const tab = this.activeTarget
    const scroller = this.scrollerTarget
    // Center the active tab within the scroll viewport without scrolling the
    // whole page (scrollIntoView would also scroll the document on load).
    const offset = tab.offsetLeft - (scroller.clientWidth - tab.clientWidth) / 2
    scroller.scrollLeft = Math.max(0, offset)
  }

  // Toggle .is-scrollable / .scrolled / .at-end on the nav so the CSS can show
  // or hide the right (›) and left (‹) overflow hints.
  updateScrollState() {
    if (!this.hasScrollerTarget) return
    const s = this.scrollerTarget
    const overflowing = s.scrollWidth - s.clientWidth > 1
    const atEnd = s.scrollLeft >= s.scrollWidth - s.clientWidth - 1
    const scrolled = s.scrollLeft > 1
    this.element.classList.toggle("is-scrollable", overflowing)
    this.element.classList.toggle("at-end", atEnd)
    this.element.classList.toggle("scrolled", scrolled)
  }

  scrollForward(event) {
    event.preventDefault()
    this.nudge(1)
  }

  scrollBack(event) {
    event.preventDefault()
    this.nudge(-1)
  }

  // Scroll the row by ~70% of the visible width in the given direction.
  nudge(direction) {
    if (!this.hasScrollerTarget) return
    const s = this.scrollerTarget
    s.scrollBy({ left: direction * s.clientWidth * 0.7, behavior: "smooth" })
  }
}
